/**
 * mb-comparison.js — BASIS. Comparison Table
 *
 * Features:
 * - Magic shine border ring — JS rAF animation (works in preview + theme editor)
 * - IntersectionObserver: rows + icons stagger in on scroll
 * - Brand-column ambient glow overlay
 */

(function () {
  'use strict';

  /* ── Init a single comparison section ──────────────────────── */
  function initSection(section) {
    const tilt    = section.querySelector('[data-cmp-tilt]');
    const scene   = section.querySelector('[data-cmp-scene]');
    const rows    = section.querySelectorAll('.mb-cmp-row');
    const icons   = section.querySelectorAll('.mb-cmp-check, .mb-cmp-cross, .mb-cmp-val-text');
    const glow    = section.querySelector('.mb-cmp-brand-glow');

    const enableAnim = section.dataset.cmpAnim !== 'false';

    /* ── Position ambient glow over brand column ── */
    function positionGlow() {
      if (!glow) return;
      const brandTh = section.querySelector('.mb-cmp-th--brand');
      if (!brandTh || !scene) return;

      const sceneRect = scene.getBoundingClientRect();
      const thRect    = brandTh.getBoundingClientRect();

      const left  = thRect.left  - sceneRect.left;
      const width = thRect.width;

      glow.style.left  = (left  - 40) + 'px';
      glow.style.width = (width + 80) + 'px';
    }

    positionGlow();

    /* Read ring width from section data attribute — set by Liquid */
    var BW = parseInt(section.dataset.cmpRingWidth, 10) || 4;
    var GW = 14;  /* glow extension beyond ring on each side   */

    /* ── Perimeter-to-angle mapping ────────────────────────────────
       Converts a fractional position t [0,1) along the rectangle's
       perimeter into the conic-gradient "from" angle so the bright
       spot sweeps at uniform *perimeter* speed (not angular speed).
       Without this, a tall rectangle makes the sweep look fast at
       the top/bottom corners and slow on the long side edges.
    ──────────────────────────────────────────────────────────────── */
    function perimAngle(t, w, h) {
      var perim = 2 * (w + h);
      var d     = (((t % 1) + 1) % 1) * perim;   /* distance along perimeter */
      var x, y;
      if      (d < w)           { x = d - w / 2;         y = -h / 2;             }
      else if (d < w + h)       { x =  w / 2;             y = (d - w) - h / 2;   }
      else if (d < 2 * w + h)   { x = w / 2 - (d-w-h);   y =  h / 2;             }
      else                      { x = -w / 2;              y = h / 2-(d-2*w-h);   }
      return (Math.atan2(x, -y) * 180 / Math.PI + 360) % 360;
    }

    function positionShineRing() {
      var shine    = section.querySelector('[data-cmp-shine]');
      var glowEl   = section.querySelector('[data-cmp-shine-glow]');
      var brandTh  = section.querySelector('.mb-cmp-th--brand');
      var brandTds = section.querySelectorAll('.mb-cmp-td--brand');
      var lastTd   = brandTds.length ? brandTds[brandTds.length - 1] : null;

      if (!brandTh || !tilt) return;

      var tiltRect   = tilt.getBoundingClientRect();
      var thRect     = brandTh.getBoundingClientRect();
      var bottomRect = lastTd ? lastTd.getBoundingClientRect() : thRect;

      /* Column bounds relative to .mb-cmp-tilt */
      var colLeft   = thRect.left   - tiltRect.left;
      var colTop    = thRect.top    - tiltRect.top;
      var colWidth  = thRect.width;
      var colHeight = bottomRect.bottom - thRect.top;

      /* Ring: BW overflow on every side */
      if (shine) {
        shine.style.left   = (colLeft  - BW) + 'px';
        shine.style.top    = (colTop   - BW) + 'px';
        shine.style.width  = (colWidth + BW * 2) + 'px';
        shine.style.height = (colHeight + BW * 2) + 'px';
      }

      /* Glow: GW extension beyond the ring on every side */
      if (glowEl) {
        glowEl.style.left   = (colLeft  - GW) + 'px';
        glowEl.style.top    = (colTop   - GW) + 'px';
        glowEl.style.width  = (colWidth + GW * 2) + 'px';
        glowEl.style.height = (colHeight + GW * 2) + 'px';
      }
    }

    /* ── Robust positioning — multiple triggers for theme editor ── */
    requestAnimationFrame(function () {
      requestAnimationFrame(positionShineRing);
    });
    setTimeout(positionShineRing, 300);
    setTimeout(positionShineRing, 800);
    window.addEventListener('load', positionShineRing, { once: true, passive: true });
    window.addEventListener('resize', positionShineRing, { passive: true });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(positionShineRing);
    }

    /* ── rAF ring animation — JS-driven, works everywhere ──────────
       Time-based (3s per rotation) — frame-rate independent.
       Updates --mb-ring-angle on the ring element every frame.
    ──────────────────────────────────────────────────────────────── */
    var shine = section.querySelector('[data-cmp-shine]');
    if (shine) {
      var ringStart = null;
      var SPOT_DEG  = 0.83 * 360;   /* white peak lives at 83% in the gradient  */
      var speedMs   = parseInt(section.dataset.cmpRingSpeed, 10) || 4000;

      function animateRing(ts) {
        if (!ringStart) ringStart = ts;
        var t         = ((ts - ringStart) / speedMs) % 1;
        var ringW     = parseFloat(shine.style.width)  || 0;
        var ringH     = parseFloat(shine.style.height) || 0;
        var fromAngle;

        if (ringW > 0 && ringH > 0) {
          /* Map perimeter position → conic "from" angle for uniform speed */
          var spotAngle = perimAngle(t, ringW, ringH);
          fromAngle = ((spotAngle - SPOT_DEG) % 360 + 360) % 360;
        } else {
          /* Fallback before ring dimensions are known */
          fromAngle = (t * 360) % 360;
        }

        shine.style.setProperty('--mb-ring-angle', fromAngle.toFixed(2) + 'deg');
        requestAnimationFrame(animateRing);
      }
      requestAnimationFrame(animateRing);
    }

    /* ── Row scroll-reveal ── */
    if (rows.length) {
      if (enableAnim) {
        const rowObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            const row   = entry.target;
            const index = Array.from(rows).indexOf(row);
            setTimeout(function () {
              row.classList.add('is-visible');
            }, index * 55);
            rowObserver.unobserve(row);
          });
        }, { threshold: 0.12, rootMargin: '0px 0px -32px 0px' });

        rows.forEach(function (row) { rowObserver.observe(row); });
      } else {
        rows.forEach(function (row) { row.classList.add('is-visible'); });
      }
    }

    /* ── Icon spring entrance ── */
    if (icons.length) {
      const iconObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          const el    = entry.target;
          const row   = el.closest('.mb-cmp-row');
          const index = row ? Array.from(rows).indexOf(row) : 0;
          setTimeout(function () {
            el.classList.add('is-visible');
          }, (index * 55) + 120);
          iconObserver.unobserve(el);
        });
      }, { threshold: 0.3, rootMargin: '0px 0px -20px 0px' });

      if (enableAnim) {
        icons.forEach(function (icon) { iconObserver.observe(icon); });
      } else {
        icons.forEach(function (icon) { icon.classList.add('is-visible'); });
      }
    }
  }

  /* ── Bootstrap ── */
  function init() {
    document.querySelectorAll('[data-cmp-section]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ── Shopify theme editor events ── */
  function handleEditorEvent(e) {
    var s = e.target.querySelector('[data-cmp-section]');
    if (s) initSection(s);
  }
  document.addEventListener('shopify:section:load',    handleEditorEvent);
  document.addEventListener('shopify:section:select',  handleEditorEvent);
  document.addEventListener('shopify:section:reorder', handleEditorEvent);

})();
